import { AnimateSharedLayout, motion } from "framer-motion";
import { FC, useState } from "react";
import { Product } from "../lib/types";
import { formatCurrency } from "../lib/utils";

type Props = {
  product: Product;
  selectedProducts: Product[];
  selectAction: (p: Product[]) => void;
  force?: boolean;
};
export const ProductCard: FC<Props> = ({
  selectAction,
  selectedProducts,
  product,
  force = false,
}) => {
  const [selected, setSelected] = useState(false);

  if (selectedProducts.length > 0 && !selected) return null;

  // console.log("Product in card:", product);
  return (
    <AnimateSharedLayout>
      <div className="flex">
        {selected === true ? (
          <motion.div
            layout="position"
            className="w-full shadow-xl p-8 rounded-lg"
            style={{ backgroundColor: "#374F5A" }}
          >
            <div className="flex flex-row justify-between items-end">
              <div className="flex flex-col text-white">
                <div>
                  <motion.div layout layoutId="title" className="text-lg">
                    {product.title} {product.title2}
                  </motion.div>
                </div>
                <div>
                  <motion.div
                    layout
                    layoutId="price"
                    className="text-lg font-bold"
                  >
                    {product.product_prices.length > 0 ? (
                      <>
                        {product.product_prices[0].price.price === 0 ? (
                          <>Kostnadsfritt</>
                        ) : (
                          <>
                            {product.product_prices[0].price.varying_price &&
                              "Från "}
                            {formatCurrency(
                              product.product_prices[0].price.price
                            )}
                          </>
                        )}
                        {product.product_prices[0].price.interval === 1 &&
                          " / mån"}
                        {product.product_prices[0].price.interval === 12 &&
                          " / år"}
                      </>
                    ) : (
                      <>pris saknas</>
                    )}
                  </motion.div>
                </div>
                <div>{product.delivery_public}</div>
              </div>
              <div>
                <motion.button
                  layout
                  type="button"
                  layoutId="action"
                  className=" rounded-full px-4 py-1 inline-block bg-white"
                  style={{ color: "#374F5A" }}
                  onClick={() => {
                    setSelected(false);
                    selectAction([]);
                  }}
                >
                  Ändra
                </motion.button>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            layout="position"
            className="w-full shadow-xl flex flex-col rounded-lg overflow-hidden"
          >
            {/* Header */}
            <div
              className="relative h-36 flex items-center px-8"
              style={{
                backgroundImage:
                  "url('https://images.unsplash.com/photo-1528962862197-29c4f24ccc04?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80')",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              {product.highlight && (
                <div className="absolute z-20 py-2 left-8 text-center right-8 -bottom-5 bg-accent-400 text-white">
                  {product.highlight}
                </div>
              )}
              <div className="text-white z-10">
                <div className="text-yellow-500 font-bold text-sm">
                  {product.subtitle}
                </div>
                <motion.div layout layoutId="title" className="text-lg">
                  {product.title} {product.title2}
                </motion.div>
                <div className="text-xs">{product.delivery_public}</div>
              </div>
              <div
                className="absolute w-full h-full"
                style={{
                  top: 0,
                  left: 0,
                  backgroundColor: "rgba(0,0,0,0.6)",
                  zIndex: 0,
                }}
              />
            </div>
            {/* Info */}
            <div className="flex-grow flex flex-col justify-between ">
              <div className="pt-8 px-8">
                {/* tabs */}

                <div className="text-sm">{product.short_description || ""}</div>
              </div>
              {/* Actions */}
              <div className="px-8 pb-8">
                {/* knappar */}
                <hr className="my-4" />
                <div className="flex justify-between flex-col">
                  <div className="flex flex-row justify-between items-center">
                    <motion.div
                      layout
                      layoutId="price"
                      className="text-2xl font-base font-bold"
                    >
                      {product.org_price && (
                        <div className="text-sm line-through text-gray-500 -mb-1">
                          {formatCurrency(product.org_price)}
                        </div>
                      )}
                      {product.product_prices.length > 0 ? (
                        <>
                          {product.product_prices[0].price.price === 0 ? (
                            <>Kostnadsfritt</>
                          ) : (
                            <>
                              {product.product_prices[0].price.varying_price &&
                                "Från "}
                              {formatCurrency(
                                product.product_prices[0].price.price
                              )}
                            </>
                          )}
                          {product.product_prices[0].price.interval === 1 &&
                            " / mån"}
                          {product.product_prices[0].price.interval === 12 &&
                            " / år"}
                        </>
                      ) : (
                        <>pris saknas</>
                      )}
                    </motion.div>
                    <motion.button
                      type="button"
                      layout
                      layoutId="action"
                      className="bg-yellow-700 rounded-full px-4 py-1 text-white inline-block text-xs"
                      style={{ backgroundColor: "#374F5A" }}
                      onClick={() => {
                        setSelected(true);
                        selectAction([...selectedProducts, product]);
                      }}
                    >
                      Välj denna produkt
                    </motion.button>
                  </div>
                  {/* Lilltexten */}
                  <div className="text-xs italic mt-4">
                    {product.legal_notes ||
                      "TEX: Leverans sker under förutsättning att samförläggning kan genomföras, dock senast 2023-12-31. Du faktureras när installationen är färdigställd."}
                  </div>
                </div>
                <hr className="my-4" />
                <div className="flex flex-row gap-4 text-sm font-medium">
                  {product.more_link && (
                    <div>
                      <a
                        href={product.more_link}
                        rel="noreferrer"
                        target="_blank"
                      >
                        Mer om produkten &rarr;
                      </a>
                    </div>
                  )}
                  {product.terms_link && (
                    <div>
                      <a
                        href={product.terms_link}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Avtalsvillkor &rarr;
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </AnimateSharedLayout>
  );
};
